import React, { useEffect, useCallback, useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { RouteProp, useFocusEffect, useRoute, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import CustomText from "../components/common/general/CustomText/CustomText";
import Loader from "../components/common/status/Loader/Loader";
import "@livekit/components-styles";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { usePostHog } from "posthog-js/react";
import BlankLayout from "../layouts/BlankLayout";
import AssessmentMenuContainer from "../containers/navigation/AssessmentMenuContainer/AssessmentMenuContainer";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import AssessmentBeginModal from "../containers/assessment/AssessmentBeginModal";
import {
  useGetAssessmentQuery,
  useGetCourseByIdQuery,
  useGetRunningAssessmentSubmissionByUserQuery,
} from "../graphql/generated/graphql";
import AssessmentCompleteModal from "../containers/assessment/AssessmentCompleteModal";
import ExitBeforeCompletedModal from "../containers/assessment/ExitBeforeCompletedModal";
import CursorLeavesWindowModal from "../containers/assessment/CursorLeavesWindowModal";
import AssessmentInProgressModal from "../containers/assessment/AssessmentInProgressModal";
import AssessmentMessageHandler from "../containers/assessment/AssessmentMessageHandler";
import AssessmentErrorModal from "../containers/assessment/AssessmentErrorModal";
import SimpleVoiceAssistant from "../containers/assessment/AssessmentSimpleVoiceAssistant";
import { fetchLiveKitToken, LiveKitActionMessageType } from "../containers/assessment/AssessmentUtils";
import { sendLiveKitMessage } from "../containers/assessment/AssessmentUtils";
import { AssessmentNoiseFilter } from "../containers/assessment/AssessmentNoiseFilter";
import QuestionCounter from "../containers/assessment/AssessmentQuestionCounter";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import {
  QUESTION_COUNTER_HEIGHT,
  SIMPLE_VOICE_ASSISTANT_HEIGHT,
  ASSESSMENT_MENU_HEIGHT,
  ASSESSMENT_MENU_HEIGHT_MOBILE,
  BOTTOM_SPACING_HEIGHT,
  CONTENT_CONTAINER_PADDING,
} from "../constants/assessmentConstants";
import { normalizeTimeFormat } from "../utils/timeUtils";
import AssessmentSpeakingOrb from "../containers/assessment/AssessmentSpeakingOrb";

function CustomCourseAssessmentStudentView() {
  const posthog = usePostHog();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Assessment">>();
  const { assessment_id: assessmentId, course_id: courseId, integration_id: integrationId } = route.params;

  const [beginModalVisible, setBeginModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [anotherAssessmentInProgressModalVisible, setAnotherAssessmentInProgressModalVisible] = useState(false);
  const [cursorLeavesWindowModalVisible, setCursorLeavesWindowModalVisible] = useState(false);
  const [liveKitToken, setLiveKitToken] = useState("");
  const [orbLoading, setOrbLoading] = useState(true);
  const [agentWarmingUp, setAgentWarmingUp] = useState(false);
  const [displayTime, setDisplayTime] = useState<string | null>(null);
  const [isTerminatingAssessment, setIsTerminatingAssessment] = useState(false);
  const [assessmentErrorModalVisible, setAssessmentErrorModalVisible] = useState(false);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [roomInstance, setRoomInstance] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [assessmentTimeLimit, setAssessmentTimeLimit] = useState<number | null>(null);
  const [assessmentNumberOfQuestions, setAssessmentNumberOfQuestions] = useState<number | null>(null);
  const { isMedium } = useResponsiveScreen();
  const { height } = Dimensions.get("window");
  const dynamicHeight = Math.max(
    5,
    height -
      QUESTION_COUNTER_HEIGHT -
      SIMPLE_VOICE_ASSISTANT_HEIGHT -
      (isMedium ? ASSESSMENT_MENU_HEIGHT : ASSESSMENT_MENU_HEIGHT_MOBILE) -
      BOTTOM_SPACING_HEIGHT -
      CONTENT_CONTAINER_PADDING * 2
  );

  const handleRoomReady = useCallback((room) => {
    setRoomInstance(room);
  }, []);

  const handleMicrophoneSelect = (deviceId: string) => {
    setSelectedMicrophoneId(deviceId);
  };

  const { refetch: refetchCourseData } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const wsURL = process.env.LIVEKIT_URL;

  const { data: assessment, refetch: refetchAssessment } = useGetAssessmentQuery({
    variables: {
      assessmentId: assessmentId,
    },
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: assessmentSubmission,
    loading: assessmentSubmissionLoading,
    error: assessmentSubmissionError,
    refetch: refetchAssessmentSubmission,
  } = useGetRunningAssessmentSubmissionByUserQuery({
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const activeAssessmentId = roomInstance?.roomInfo?.name.split("assessment-")[1]?.split("-user-")[0];

  const assessmentIsRunning =
    displayTime !== "00:00" &&
    assessmentTimeLimit &&
    displayTime !== normalizeTimeFormat(`${assessmentTimeLimit}:00`) &&
    !!activeAssessmentId &&
    activeAssessmentId === assessmentId;

  const assessmentSubmissionNotExistsInDatabase = !assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data;
  const assessmentSubmissionIsRunningInDatabase =
    assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data?.status === "RUNNING";

  const userAlreadyHasAnotherRunningAssessment =
    assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data?.assessment?.id &&
    assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data?.assessment?.id !== assessmentId;

  const handleLiveKitTokenError = () => {
    setAgentWarmingUp(false);
    setBeginModalVisible(false);
    setAssessmentErrorModalVisible(true);
    console.error("Error fetching LiveKit token");
  };

  const handleLiveKitTokenFetched = async (token: string) => {
    setLiveKitToken(token);
  };

  useEffect(() => {
    posthog?.capture("Assessment Page Visited");
  }, []);

  useFocusEffect(
    useCallback(() => {
      const initializePageState = async () => {
        const assessmentIsActive = !!liveKitToken;
        if (assessmentSubmission === undefined) {
          //wait for assessmentSubmission to be fetched
          return;
        }

        if (userAlreadyHasAnotherRunningAssessment) {
          setAnotherAssessmentInProgressModalVisible(true);
          return;
        }

        if (assessmentIsActive) {
          return;
        }

        if (assessmentSubmissionIsRunningInDatabase && !liveKitToken) {
          setAgentWarmingUp(true);
          await fetchLiveKitToken(assessmentId, handleLiveKitTokenFetched, handleLiveKitTokenError);
          return;
        }

        const showBeginModal = assessmentSubmissionNotExistsInDatabase && !activeAssessmentId && !liveKitToken;

        if (showBeginModal) {
          setBeginModalVisible(true);
          return;
        }
      };

      if (!assessmentSubmissionLoading) {
        initializePageState();
      }
    }, [assessmentId, assessmentSubmissionLoading, assessmentSubmission, liveKitToken, activeAssessmentId])
  );

  const handleVisibilityChange = useCallback(() => {
    if (!assessmentIsRunning) {
      return;
    }
    if (document.hidden) {
      setCursorLeavesWindowModalVisible(true);
    }
  }, [assessmentIsRunning]);

  const handleMouseLeave = useCallback(
    (event) => {
      if (!assessmentIsRunning) {
        return;
      }
      const { clientX, clientY } = event;

      const leftEdge = clientX <= 0;
      const rightEdge = clientX >= window.innerWidth;
      const topEdge = clientY <= 0;
      const bottomEdge = clientY >= window.innerHeight;

      if (leftEdge || rightEdge || topEdge || bottomEdge) {
        setCursorLeavesWindowModalVisible(true);
      }
    },
    [assessmentIsRunning]
  );

  useFocusEffect(
    useCallback(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [assessmentIsRunning])
  );

  useFocusEffect(
    useCallback(() => {
      if (!assessmentId) {
        return;
      }
      refetchAssessment();
      refetchAssessmentSubmission();
    }, [assessmentId])
  );

  useFocusEffect(
    useCallback(() => {
      if (assessment?.getAssessment?.data) {
        setAssessmentTimeLimit(assessment.getAssessment.data.timeLimit);
        setAssessmentNumberOfQuestions(assessment.getAssessment.data.numberOfQuestions);
        setDisplayTime(normalizeTimeFormat(`${assessment.getAssessment.data.timeLimit}:00`));
      }
    }, [assessment?.getAssessment?.data])
  );

  if (assessmentSubmissionLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }
  if (assessmentSubmissionError || !assessment?.getAssessment?.data) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </BlankLayout>
    );
  }
  const assessmentHasNoQuestions = assessment?.getAssessment?.data?.aiTutors?.length === 0;
  if (assessmentHasNoQuestions) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="This assessment is not enabled." />
        </View>
      </BlankLayout>
    );
  }

  const handleExitPage = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = route.params?.module_id;
      if (moduleId && typeof moduleId === "string") {
        const integrationId = typeof route.params?.integration_id === "string" ? route.params.integration_id : courseId;
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: courseId,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: courseId,
          integration_id: integrationId,
        });
      }
    }
  };

  const handleBeginAssessment = async () => {
    if (assessmentIsRunning) {
      console.error("Assessment is already running or cleanup is in progress. Try again in a few seconds.");
      return;
    }
    setAgentWarmingUp(true);
    await fetchLiveKitToken(assessmentId, handleLiveKitTokenFetched, handleLiveKitTokenError);
    setBeginModalVisible(false);
  };

  const handleCancelAssessmentBegin = () => {
    setBeginModalVisible(false);
    handleExitPage();
  };

  const handleAssessmentComplete = () => {
    setAgentWarmingUp(false);
    setCompleteModalVisible(true);
    setDisplayTime("00:00");
  };

  const handleConfirmCompleteModal = () => {
    setCompleteModalVisible(false);
    refetchCourseData();
    setDisplayTime(normalizeTimeFormat(`${assessmentTimeLimit}:00`));
    setLiveKitToken("");
    setRoomInstance(null);
    handleExitPage();
  };

  const handleTerminateAssessment = async () => {
    if (!roomInstance || !assessmentId) {
      console.error("Cannot terminate assessment - room or assessmentId unavailable");
      return;
    }
    setIsTerminatingAssessment(true);
    setExitModalVisible(false);
    await sendLiveKitMessage(LiveKitActionMessageType.TerminateAssessment, roomInstance, assessmentId);
    await refetchCourseData();
    setLiveKitToken("");
    setRoomInstance(null);
    setDisplayTime(normalizeTimeFormat(`${assessmentTimeLimit}:00`));
    setIsTerminatingAssessment(false);
    setAgentWarmingUp(false);
    handleExitPage();
  };

  const handleAnotherAssessmentInProgressModalClick = () => {
    setAnotherAssessmentInProgressModalVisible(false);
    handleExitPage();
  };

  const handleExitButtonPress = () => {
    if (assessmentIsRunning) {
      setExitModalVisible(true);
    } else {
      handleExitPage();
    }
  };

  const handleAssessmentErrorModalClick = () => {
    setAssessmentErrorModalVisible(false);
    handleExitPage();
  };

  const handleErrorInLiveKitAgent = () => {
    setDisplayTime(normalizeTimeFormat(`${assessmentTimeLimit}:00`));
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("mouseleave", handleMouseLeave);
    setAssessmentErrorModalVisible(true);
  };

  const handleTimerUpdated = (time: string) => {
    if (agentWarmingUp) {
      setAgentWarmingUp(false);
    }
    setDisplayTime(normalizeTimeFormat(time));
  };

  const handleQuestionIndexUpdate = (index: number) => {
    setQuestionIndex(index);
  };

  const showAssessmentAsRunningOrLoading =
    !assessmentErrorModalVisible &&
    !anotherAssessmentInProgressModalVisible &&
    !beginModalVisible &&
    !completeModalVisible;

  return (
    <BlankLayout>
      {liveKitToken ? (
        <LiveKitRoom
          style={styles.liveKitContainer}
          token={liveKitToken}
          serverUrl={wsURL}
          connect={true}
          audio={{ deviceId: selectedMicrophoneId }}>
          <AssessmentMenuContainer
            onComplete={handleAssessmentComplete}
            handleExitPress={handleExitButtonPress}
            defaultTime={assessmentTimeLimit}
            displayTime={displayTime}
            activeAssessmentId={activeAssessmentId}
            setDisplayTime={setDisplayTime}
            isTerminatingAssessment={isTerminatingAssessment}
            hasMicrophoneControls={true}
          />
          <View style={styles.contentContainer}>
            {agentWarmingUp && <CustomText text="Agent is warming up... This may take a few seconds." size="xl" />}

            <AssessmentMessageHandler
              onComplete={handleAssessmentComplete}
              onTimerUpdated={handleTimerUpdated}
              onTimerStarted={handleTimerUpdated}
              assessmentId={assessmentId}
              onRoomReady={handleRoomReady}
              assessmentIsRunning={assessmentIsRunning}
              onErrorCallback={handleErrorInLiveKitAgent}
              onQuestionIndexUpdate={handleQuestionIndexUpdate}
            />
            <AssessmentNoiseFilter />
            {showAssessmentAsRunningOrLoading && (
              <AssessmentSpeakingOrb
                dynamicHeight={dynamicHeight}
                orbLoading={orbLoading}
                setOrbLoading={setOrbLoading}
              />
            )}
            <QuestionCounter questionIndex={questionIndex} totalQuestions={assessmentNumberOfQuestions} />
            <SimpleVoiceAssistant questionIndex={questionIndex} />
            <View style={{ height: BOTTOM_SPACING_HEIGHT }} />
            <RoomAudioRenderer />
          </View>
        </LiveKitRoom>
      ) : (
        <View style={styles.pageContainer}>
          <AssessmentMenuContainer
            onComplete={handleAssessmentComplete}
            handleExitPress={handleExitButtonPress}
            defaultTime={assessmentTimeLimit}
            displayTime={displayTime}
            activeAssessmentId={activeAssessmentId}
            setDisplayTime={setDisplayTime}
            isTerminatingAssessment={isTerminatingAssessment}
            hasMicrophoneControls={false}
          />
          <View style={styles.contentContainer}>{showAssessmentAsRunningOrLoading && <Loader />}</View>
        </View>
      )}
      <AssessmentBeginModal
        visible={beginModalVisible}
        onCancel={handleCancelAssessmentBegin}
        onConfirm={handleBeginAssessment}
        defaultTime={assessmentTimeLimit}
        defaultQuestionsCount={assessmentNumberOfQuestions}
        isLoading={agentWarmingUp}
        selectedDeviceId={selectedMicrophoneId}
        onDeviceSelect={handleMicrophoneSelect}
      />
      <AssessmentCompleteModal visible={completeModalVisible} onConfirm={handleConfirmCompleteModal} />
      <ExitBeforeCompletedModal
        visible={exitModalVisible}
        onConfirm={handleTerminateAssessment}
        onCancel={() => setExitModalVisible(false)}
      />
      <CursorLeavesWindowModal
        visible={cursorLeavesWindowModalVisible}
        onConfirm={() => setCursorLeavesWindowModalVisible(false)}
      />
      <AssessmentInProgressModal
        visible={anotherAssessmentInProgressModalVisible}
        onConfirm={handleAnotherAssessmentInProgressModalClick}
      />
      <AssessmentErrorModal visible={assessmentErrorModalVisible} onConfirm={handleAssessmentErrorModalClick} />
    </BlankLayout>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    padding: CONTENT_CONTAINER_PADDING,
    backgroundColor: "#E1E5FA",
  },
  liveKitContainer: {
    flex: 1,
    backgroundColor: "#E1E5FA",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default CustomCourseAssessmentStudentView;
